import { StateCreator } from "zustand";
import { PERSIST_STORE_KEY } from "@/constants";
import { sliceResetFns } from "./utils";
import { CombinedSlice } from "./types";
import { ClientPreferenceState, ClientPreferenceActions } from "@/types/auth";

export interface ClientPreferenceSlice
  extends ClientPreferenceState, ClientPreferenceActions {}

// Hard-coded defaults. Used both as the fallback when no persisted value is
// found AND as the target of resetAllSlices() (logout) — resetting must NOT
// restore the user's pre-logout preferences.
const DEFAULTS: ClientPreferenceState = {
  selectedOrganizationId: null,
  sidebarCollapsed: false,
  sidebarHovered: false,
};

// Read the persisted slice state directly from localStorage so the in-memory
// initial state reflects what was last persisted, with the hard-coded defaults
// as fallback.
const readPersistedPreferences = (): Partial<ClientPreferenceState> => {
  try {
    const raw = localStorage.getItem(PERSIST_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as { state?: Partial<ClientPreferenceState> };
    return parsed.state ?? {};
  } catch {
    return {};
  }
};

const getInitialState = (): ClientPreferenceState => {
  const persisted = readPersistedPreferences();
  return {
    selectedOrganizationId:
      persisted.selectedOrganizationId ?? DEFAULTS.selectedOrganizationId,
    sidebarCollapsed: persisted.sidebarCollapsed ?? DEFAULTS.sidebarCollapsed,
    sidebarHovered: persisted.sidebarHovered ?? DEFAULTS.sidebarHovered,
  };
};

/**
 * Client Preference Slice
 * Manages client-side user preferences (persisted via Zustand persist)
 * - selectedOrganizationId: Currently selected organization
 * - sidebarCollapsed: UI state for sidebar visibility
 */
export const createClientPreferenceSlice: StateCreator<
  CombinedSlice,
  [
    ["zustand/devtools", never],
    ["zustand/persist", unknown],
    ["zustand/immer", never],
  ],
  [],
  ClientPreferenceSlice
> = (set) => {
  // Reset target is DEFAULTS, not the captured initial state — otherwise
  // logout's resetAllSlices() would restore the user's pre-logout preferences.
  sliceResetFns.add(() => set(DEFAULTS));

  return {
    ...getInitialState(),

    selectOrganization: (id) => {
      set((state) => {
        state.selectedOrganizationId = id;
      });
    },

    setSidebarCollapsed: (collapsed) => {
      set((state) => {
        state.sidebarCollapsed = collapsed;
      });
    },

    setSidebarHovered: (hovered) => {
      set((state) => {
        state.sidebarHovered = hovered;
      });
    },
  };
};
